import { ErrorOutline, History, KeyboardArrowDown } from "@mui/icons-material";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { ERROR_MESSAGES, LoadState } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";
import { IGeneralHistory } from "../../redux/common/common.types";
import { fetchHistoryByTableColumnAsync } from "../../redux/common/commonActions";
import { IStoreState } from "../../redux/initialStoreState";
import { IAcitvityHistoryLogsProps } from "./Activity.types";
import { HistoryRow } from "../../views/leads/LeadHistory/HistoryRow";
import { groupHistoryRecords } from "../../helpers/historyHelpers";

export const ActivityHistoryLogs: React.FC<IAcitvityHistoryLogsProps> = (
  props,
) => {
  const { moduleInformation } = props;

  const { list, data, loading } = useSelector(
    (storeState: IStoreState) => storeState.common.generalHistory,
  );

  const theme = useTheme();
  const dispatch = useDispatchWrapper();

  const [visibleCount, setVisibleCount] = React.useState<number>(4);

  const fetchHistory = React.useCallback(async () => {
    if (moduleInformation?.moduleId) {
      dispatch(fetchHistoryByTableColumnAsync(moduleInformation.moduleId));
    }
  }, [moduleInformation?.moduleId, dispatch]);

  React.useEffect(() => {
    if (moduleInformation?.moduleId) {
      fetchHistory();
    }
  }, [moduleInformation?.moduleId, fetchHistory]);

  const historyList: IGeneralHistory[] = React.useMemo(() => {
    if (Array.isArray(list) && list.length > 0) return list;
    if (data?.result && Array.isArray(data.result)) return data.result;
    if (Array.isArray(data)) return data;
    return [];
  }, [list, data]);

  const groupedHistory = React.useMemo(
    () => groupHistoryRecords(historyList),
    [historyList],
  );

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 4);
  };

  const hasMore = groupedHistory.length > visibleCount;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Stack
        padding={1}
        sx={{
          backgroundColor: theme.palette.grey[200],
          borderRadius: 1,
          display: "flex",
          flexDirection: "row",
          gap: 1,
          mb: 2,
          flexShrink: 0,
        }}
      >
        <History
          color="primary"
          fontSize="medium"
          sx={{ marginTop: "auto", marginBottom: "auto" }}
        />
        <Typography variant="h4" fontWeight={600}>
          History Logs
        </Typography>
      </Stack>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 0.5,
          pb: hasMore ? 7 : 2,
        }}
      >
        <Stack direction={"column"} spacing={1.5}>
          {loading === LoadState.InProgress && (
            <>
              <Skeleton height={"40px"} sx={{ borderRadius: 2 }} />
              <Skeleton height={"40px"} sx={{ borderRadius: 2 }} />
              <Skeleton height={"40px"} sx={{ borderRadius: 2 }} />
              <Skeleton height={"40px"} sx={{ borderRadius: 2 }} />
              <Skeleton height={"40px"} sx={{ borderRadius: 2 }} />
            </>
          )}
          {loading === LoadState.Loaded && historyList.length === 0 && (
            <Box
              minHeight={120}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box display={"flex"} justifyContent={"center"} mb={1}>
                <ErrorOutline />
              </Box>
              <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                <Typography
                  variant="body1"
                  textAlign={"center"}
                  fontWeight={600}
                >
                  {ERROR_MESSAGES.NO_RECORD_FOUND}
                </Typography>
              </Box>
            </Box>
          )}

          {loading === LoadState.Loaded &&
            groupedHistory.slice(0, visibleCount).map((group, index, arr) => {
              return (
                <HistoryRow
                  key={group.groupId}
                  group={group}
                  isLast={index === arr.length - 1}
                />
              );
            })}
        </Stack>
      </Box>

      {loading === LoadState.Loaded && hasMore && (
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleShowMore}
            endIcon={<KeyboardArrowDown />}
            sx={{
              pointerEvents: "auto",
              borderRadius: "20px",
              px: 3,
              py: 0.75,
              fontWeight: 600,
              fontSize: "0.85rem",
              textTransform: "none",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: theme.shadows?.[4] || "0px 4px 12px rgba(0,0,0,0.15)",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.shadows?.[6] || "0px 6px 16px rgba(0,0,0,0.22)",
              },
            }}
          >
            Show More ({groupedHistory.length - visibleCount} remaining)
          </Button>
        </Box>
      )}
    </Box>
  );
};


